import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
// How long to wait after the last +/-/input change on a line before
// submitting one consolidated /cart/change.js request for it. Per line, not
// global -- see #pendingLineUpdates.
const CART_QUANTITY_DEBOUNCE_MS = 500;

class CartItemsComponent extends Component {
  /**
   * Per-line pending-update state, keyed by line number. Rapid clicks on one
   * line only ever reset that line's own timer/target, so a burst on line A
   * can never cancel or merge with a pending change on line B.
   *
   * intentVersion increments on EVERY optimistic click (not just when a
   * request is committed) -- this is what lets a new click immediately
   * invalidate an already in-flight response, before the next debounce
   * commit even happens. A request captures the intentVersion at the moment
   * it's committed; its response is only ever applied to the DOM if that
   * captured version still matches the line's current intentVersion when
   * the response arrives.
   * @type {Map<number, {
   *   targetQuantity: number,
   *   intentVersion: number,
   *   timer: number | undefined,
   *   abortController: AbortController | undefined,
   * }>}
   */
  #pendingLineUpdates = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);

    for (const pending of this.#pendingLineUpdates.values()) {
      clearTimeout(pending.timer);
      pending.abortController?.abort();
    }
    this.#pendingLineUpdates.clear();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event. The visible quantity
   * has already been updated synchronously by the quantity selector itself
   * (see assets/component-quantity-selector.js) -- this only schedules the
   * one consolidated network request for this line.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    this.#scheduleLineUpdate(line, quantity);
  };

  /**
   * Records the latest target quantity for a line, bumps its intent version,
   * and (re)starts its debounce timer. Every new call for the same line
   * resets the timer and replaces the target -- it never queues a second
   * request. Bumping intentVersion here (not just when the request is
   * eventually committed) immediately invalidates any still in-flight
   * response for this line, so a late response for an older target can
   * never morph the DOM out from under a newer optimistic click.
   * @param {number} line
   * @param {number} quantity
   */
  #scheduleLineUpdate(line, quantity) {
    let pending = this.#pendingLineUpdates.get(line);
    if (!pending) {
      pending = { targetQuantity: quantity, intentVersion: 0, timer: undefined, abortController: undefined };
      this.#pendingLineUpdates.set(line, pending);
    }

    clearTimeout(pending.timer);
    pending.targetQuantity = quantity;
    pending.intentVersion += 1;

    // Best-effort immediate cancellation of any in-flight request for this
    // line. Not required for correctness (the intentVersion check in
    // updateQuantity's response handler is the real guarantee), but avoids
    // wasted network work.
    pending.abortController?.abort();
    pending.abortController = undefined;

    this.#setLineBusy(line, true);

    pending.timer = window.setTimeout(() => this.#commitLineUpdate(line), CART_QUANTITY_DEBOUNCE_MS);
  }

  /**
   * Submits the consolidated request for a line's latest target quantity,
   * capturing the intent version at this exact moment so the eventual
   * response can be checked for staleness against whatever the line's
   * intent version has become by the time it arrives.
   * @param {number} line
   */
  #commitLineUpdate(line) {
    const pending = this.#pendingLineUpdates.get(line);
    if (!pending) return;

    const { targetQuantity, intentVersion } = pending;

    const abortController = new AbortController();
    pending.abortController = abortController;

    if (targetQuantity === 0) {
      this.onLineItemRemove(line, { signal: abortController.signal, intentVersion, requestedQuantity: 0 });
      return;
    }

    this.updateQuantity(
      { line, quantity: targetQuantity, action: 'change' },
      { signal: abortController.signal, intentVersion, requestedQuantity: targetQuantity }
    );

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Marks a line as having an unsaved/in-flight quantity change, without
   * blocking further clicks on it (no pointer-events lock) -- restrained
   * pending affordance only (aria-busy + existing shimmer on totals).
   * @param {number} line
   * @param {boolean} busy
   */
  #setLineBusy(line, busy) {
    const row = this.refs.cartItemRows[line - 1];
    row?.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * Returns true if a response captured at `intentVersion` for `requestedQuantity`
   * is still the line's current intent -- i.e. no newer click has changed the
   * target since this request was committed.
   * @param {number} line
   * @param {number} intentVersion
   * @param {number} requestedQuantity
   */
  #isResponseCurrent(line, intentVersion, requestedQuantity) {
    const pending = this.#pendingLineUpdates.get(line);
    return pending !== undefined && pending.intentVersion === intentVersion && pending.targetQuantity === requestedQuantity;
  }

  /**
   * Clears a line's pending-update bookkeeping once its request has settled
   * (successfully or not) and no newer request has superseded it.
   * @param {number} line
   * @param {number} intentVersion
   */
  #resolveLineUpdate(line, intentVersion) {
    const pending = this.#pendingLineUpdates.get(line);
    if (!pending || pending.intentVersion !== intentVersion) return;

    this.#setLineBusy(line, false);
    this.#pendingLineUpdates.delete(line);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {{signal?: AbortSignal, intentVersion?: number, requestedQuantity?: number}} [meta] - Stale-response protection when called from a debounced quantity commit. Omitted when called directly (e.g. the Remove button), in which case any pending debounced quantity change for this line is invalidated first so removal always wins and a stale request can't restore the line.
   */
  onLineItemRemove(line, meta = {}) {
    if (meta.intentVersion === undefined) {
      let pending = this.#pendingLineUpdates.get(line);
      if (pending) {
        clearTimeout(pending.timer);
        pending.abortController?.abort();
      } else {
        pending = { targetQuantity: 0, intentVersion: 0, timer: undefined, abortController: undefined };
        this.#pendingLineUpdates.set(line, pending);
      }
      pending.intentVersion += 1;
      pending.targetQuantity = 0;
      meta = { intentVersion: pending.intentVersion, requestedQuantity: 0 };
    }

    this.updateQuantity(
      {
        line,
        quantity: 0,
        action: 'clear',
      },
      meta
    );

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {{signal?: AbortSignal, version?: number}} [meta] - Optional stale-response protection for a debounced/consolidated update. Omitted for callers that don't need it (none currently synchronous), in which case the request is always treated as current.
   */
  updateQuantity(config, meta = {}) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const { signal, intentVersion, requestedQuantity } = meta;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig('json', { body }), signal })
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        // A newer click has changed this line's target since this request
        // was committed (whether or not the abort above landed in time) --
        // this response is stale. Don't touch the DOM at all: no morph, no
        // CartUpdateEvent, no rollback. The request that superseded this one
        // owns the line's final reconciliation.
        if (
          intentVersion !== undefined &&
          requestedQuantity !== undefined &&
          !this.#isResponseCurrent(line, intentVersion, requestedQuantity)
        ) {
          return;
        }

        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        // Pawpular: CartUpdateEvent above is dispatched BEFORE morphSection() runs, so a listener
        // reacting to it (e.g. the Local Delivery / Store Pickup reward UI) renders correctly and
        // then immediately has its own DOM changes overwritten by the morph that follows.
        // morphSection()'s own onAfterUpdate hook (assets/morph.js) calls updatedCallback() on the
        // newly-parsed (detached) node from the diff, not the live element that stays in the
        // document, so it never reaches a real, currently-connected component either. This event
        // fires only once the morph above has actually finished patching the live DOM, giving
        // listeners one deterministic point to re-sync from.
        this.#dispatchCartRenderComplete();

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        if (intentVersion !== undefined) {
          this.#resolveLineUpdate(line, intentVersion);
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => this.#dispatchCartRenderComplete());
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#dispatchCartRenderComplete();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => this.#dispatchCartRenderComplete());
    }
  };

  /**
   * Pawpular: fires once this cart section's DOM has actually finished being morphed into place,
   * as one deterministic point for listeners (e.g. the Local Delivery / Store Pickup reward UI)
   * to re-sync their own JS-driven state -- see the longer explanation above the first
   * morphSection() call in this file.
   */
  #dispatchCartRenderComplete() {
    document.dispatchEvent(new CustomEvent('pawpular:cart-render-complete', { detail: { sectionId: this.sectionId } }));
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
